import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUsers, FaChalkboardTeacher, FaSearch, FaPlus } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import CreateForm from './CreateForm';
import './PeerLearning.css';

const PeerLearning = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('study-groups');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [studyGroups, setStudyGroups] = useState([]);
  const [tutoringSessions, setTutoringSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual Firebase calls
      // Simulated data for now
      setStudyGroups([
        {
          id: 1,
          name: 'Advanced Mathematics Study Group',
          subject: 'Mathematics',
          description: 'Weekly study sessions for advanced calculus and linear algebra',
          meetingTime: '2024-03-20T15:00',
          maxMembers: 5,
          currentMembers: 3,
          requirements: 'Must be enrolled in MATH 300 or higher',
          createdBy: 'john@example.com'
        },
        // Add more sample study groups
      ]);

      setTutoringSessions([
        {
          id: 1,
          title: 'Physics Tutoring Session',
          subject: 'Physics',
          description: 'One-on-one tutoring for mechanics and thermodynamics',
          meetingTime: '2024-03-21T14:00',
          duration: 60,
          price: 25,
          requirements: 'Basic knowledge of physics required',
          tutor: 'sarah@example.com'
        },
        // Add more sample tutoring sessions
      ]);

      setLoading(false);
    } catch (err) {
      setError('Failed to fetch data. Please try again later.');
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    try {
      // TODO: Replace with actual Firebase calls
      if (activeTab === 'study-groups') {
        const newGroup = {
          id: Date.now(),
          ...formData,
          currentMembers: 1,
          createdBy: currentUser.email
        };
        setStudyGroups([...studyGroups, newGroup]);
      } else {
        const newSession = {
          id: Date.now(),
          ...formData,
          tutor: currentUser.email
        };
        setTutoringSessions([...tutoringSessions, newSession]);
      }
      setShowCreateForm(false);
    } catch (err) {
      setError('Failed to create. Please try again.');
    }
  };

  const handleJoin = async (id) => {
    try {
      // TODO: Replace with actual Firebase calls
      if (activeTab === 'study-groups') {
        setStudyGroups(groups =>
          groups.map(group =>
            group.id === id
              ? { ...group, currentMembers: group.currentMembers + 1 }
              : group
          )
        );
      } else {
        // Handle booking tutoring session
        setTutoringSessions(sessions =>
          sessions.map(session =>
            session.id === id
              ? { ...session, booked: true }
              : session
          )
        );
      }
    } catch (err) {
      setError('Failed to join/book. Please try again.');
    }
  };

  const filteredItems = activeTab === 'study-groups'
    ? studyGroups.filter(group =>
        group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.subject.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : tutoringSessions.filter(session =>
        session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        session.subject.toLowerCase().includes(searchQuery.toLowerCase())
      );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="peer-learning">
      <div className="peer-learning-header">
        <h1>Peer Learning</h1>
        <p>Connect with fellow students and tutors for collaborative learning</p>
      </div>

      <div className="peer-learning-tabs">
        <button
          className={`tab ${activeTab === 'study-groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('study-groups')}
        >
          <FaUsers /> Study Groups
        </button>
        <button
          className={`tab ${activeTab === 'tutoring' ? 'active' : ''}`}
          onClick={() => setActiveTab('tutoring')}
        >
          <FaChalkboardTeacher /> Tutoring Sessions
        </button>
      </div>

      <div className="peer-learning-actions">
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder={`Search ${activeTab === 'study-groups' ? 'study groups' : 'tutoring sessions'}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button
          className="create-btn"
          onClick={() => setShowCreateForm(true)}
        >
          <FaPlus /> Create {activeTab === 'study-groups' ? 'Study Group' : 'Tutoring Session'}
        </button>
      </div>

      <div className="items-grid">
        {filteredItems.map(item => (
          <motion.div
            key={item.id}
            className="item-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h3>{activeTab === 'study-groups' ? item.name : item.title}</h3>
            <p className="subject">{item.subject}</p>
            <p className="description">{item.description}</p>
            <div className="item-details">
              <p>
                <strong>Meeting Time:</strong>{' '}
                {new Date(item.meetingTime).toLocaleString()}
              </p>
              {activeTab === 'study-groups' ? (
                <p>
                  <strong>Members:</strong> {item.currentMembers}/{item.maxMembers}
                </p>
              ) : (
                <>
                  <p>
                    <strong>Duration:</strong> {item.duration} minutes
                  </p>
                  <p>
                    <strong>Price:</strong> ${item.price}/hour
                  </p>
                </>
              )}
              <p>
                <strong>Requirements:</strong> {item.requirements}
              </p>
            </div>
            <button
              className="join-btn"
              onClick={() => handleJoin(item.id)}
              disabled={
                (activeTab === 'study-groups' && item.currentMembers >= item.maxMembers) ||
                (activeTab === 'tutoring' && item.booked)
              }
            >
              {activeTab === 'study-groups'
                ? item.currentMembers >= item.maxMembers
                  ? 'Full'
                  : 'Join Group'
                : item.booked
                ? 'Booked'
                : 'Book Session'}
            </button>
          </motion.div>
        ))}
      </div>

      {showCreateForm && (
        <CreateForm
          type={activeTab}
          onSubmit={handleCreate}
          onClose={() => setShowCreateForm(false)}
        />
      )}
    </div>
  );
};

export default PeerLearning; 